// src/redux/slices/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { loadPermissions, clearPermissions } from './permissionsSlice';
import { initSession, clearSession } from '../../utils/sessionTimer';
import { API } from '../../utils/apiUrls';

export const LOGIN_ERROR_MESSAGE = 'Invalid username or password. Please check your credentials and try again.';

export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ username, password, source }, { rejectWithValue, dispatch }) => {
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_IAM_BASE_URL}${process.env.REACT_APP_LOGIN_ENDPOINT}`,
        { username, password }
      );

      const token = resp.data?.auth_token;
      if (typeof token !== 'string' || !token.trim()) {
        throw new Error('Invalid token response');
      }

      const decoded = jwtDecode(token);
      const { parentClientId, merchantId, exp } = decoded ?? {};

      if (!parentClientId || !merchantId) {
        throw new Error('Invalid token payload');
      }

      // Initialize session timing using monotonic clock + server offset.
      // This makes the session immune to incorrect client system clocks.
      initSession(decoded, resp.headers);

      try {
        localStorage.setItem('authToken', token);
        localStorage.setItem('parentClientId', parentClientId);
        localStorage.setItem('merchantId', merchantId);
      } catch (storageErr) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Auth storage write failed:', storageErr);
        }
        throw new Error('Session storage unavailable');
      }

      const sourceValue = source || localStorage.getItem('source') || 'wayuvega';
      if (sourceValue) {
        try {
          localStorage.setItem('source', sourceValue);
        } catch (_) {}
      }
      // Resolve V2 permissions for RBAC gating in the UI
      let permissionKeys = [];
      let organizationId = null;
      let userType = null;
      try {
        const permResp = await axios.get(API.iamPermissionsResolve(), {
          headers: { Authorization: token },
        });
        const snap = permResp.data;
        permissionKeys = snap?.v2_permission_keys ?? [];
        organizationId = snap?.organization_id ?? null;
        userType = snap?.user_type ?? null;
      } catch (permErr) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[Auth] Failed to resolve V2 permissions:', permErr?.message);
        }
        // Don't fail login if permission resolve fails — UI degrades gracefully
      }

      const INTERNAL_USER_TYPES = ['SUPER_ADMIN', 'WAYUVEGA_INTERNAL', 'WAYUVEGA_ADMIN'];
      const isInternalAudience =
        Number(decoded?.platformId) === 3 ||
        (userType != null && INTERNAL_USER_TYPES.includes(userType));
      if (isInternalAudience) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('parentClientId');
        localStorage.removeItem('merchantId');
        localStorage.removeItem('organizationId');
        localStorage.removeItem('permissionKeys');
        localStorage.removeItem('userType');
        clearSession();
        return rejectWithValue('This account is not authorised to access the merchant panel. Please use the admin portal.');
      }

      // Persist V2 permission snapshot so it survives page refresh
      try {
        if (organizationId != null) localStorage.setItem('organizationId', String(organizationId));
        localStorage.setItem('permissionKeys', JSON.stringify(permissionKeys));
        if (userType != null) localStorage.setItem('userType', userType);
      } catch (_) {}

      // Refresh the permission snapshot for the newly logged-in user.
      // Clear the previous user's cached snapshot first, then re-fetch — otherwise
      // the UI renders the prior user's permissions when switching accounts without
      // a full page reload (the snapshot slice is only re-hydrated on cold start).
      dispatch(clearPermissions());
      dispatch(loadPermissions());

      return { token, parentClientId, merchantId, exp, source: sourceValue, permissionKeys, organizationId, userType };
    } catch (err) {
      if (process.env.NODE_ENV !== 'production' && err?.message) {
        console.warn('[Auth]', err.message);
      }
      return rejectWithValue(LOGIN_ERROR_MESSAGE);
    }
  }
);

const initialState = {
  isAuthenticated: false,
  token: null,
  parentClientId: null,
  merchantId: null,
  userExp: null,     // Unix timestamp (in seconds) when token expires
  loading: false,
  error: null,
  initialized: false,
  // track which branded login was used. initialize from localStorage if present.
  source: localStorage.getItem('source') || 'wayuvega',
  // V2 RBAC — populated after login via GET /v2/iam/permissions/resolve
  permissionKeys: [],
  organizationId: null,
  userType: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      state.isAuthenticated = false;
      state.token = null;
      state.parentClientId = null;
      state.merchantId = null;
      state.userExp = null;
      state.error = null;
      state.initialized = true;
      state.permissionKeys = [];
      state.organizationId = null;
      state.userType = null;
      // Clear localStorage auth keys but preserve source
      localStorage.removeItem('authToken');
      localStorage.removeItem('parentClientId');
      localStorage.removeItem('merchantId');
      localStorage.removeItem('organizationId');
      localStorage.removeItem('permissionKeys');
      localStorage.removeItem('userType');
      clearSession();
    },
    restoreSession(state, action) {
      const { token, user } = action.payload;

      // Extract fields from decoded token
      const parentClientId = user.parentClientId || user.clientId;
      const merchantId = user.merchantId;
      const exp = user.exp;
      const source = user.source || localStorage.getItem('source') || 'wayuvega';

      // Restore V2 permission snapshot persisted on last login
      const storedOrgId = localStorage.getItem('organizationId');
      const organizationId = storedOrgId ? Number(storedOrgId) : null;
      let permissionKeys = [];
      try {
        const raw = localStorage.getItem('permissionKeys');
        if (raw) permissionKeys = JSON.parse(raw);
      } catch (_) {}
      const userType = localStorage.getItem('userType') || null;

      // Update Redux state
      state.isAuthenticated = true;
      state.token = token;
      state.parentClientId = parentClientId;
      state.merchantId = merchantId;
      state.userExp = exp;
      state.error = null;
      state.initialized = true;
      state.source = source;
      state.organizationId = organizationId;
      state.permissionKeys = permissionKeys;
      state.userType = userType;
      
      // 🔥 PERSIST TO LOCALSTORAGE
      try {
        localStorage.setItem('authToken', token);
        if (parentClientId) localStorage.setItem('parentClientId', parentClientId);
        if (merchantId) localStorage.setItem('merchantId', merchantId);
        localStorage.setItem('source', source);
      } catch (e) {
        console.error('Failed to save to localStorage:', e);
      }
    },
    markInitialized(state) { state.initialized = true; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        const { token, parentClientId, merchantId, exp, source, permissionKeys, organizationId, userType } = action.payload;
        state.loading = false;
        state.isAuthenticated = true;
        state.token = token;
        state.parentClientId = parentClientId;
        state.merchantId = merchantId;
        state.userExp = exp;
        state.error = null;
        state.permissionKeys = permissionKeys ?? [];
        state.organizationId = organizationId ?? null;
        state.userType = userType ?? null;
        if (source) {
          state.source = source;
          try { localStorage.setItem('source', source); } catch (e) {}
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        if (!state.isAuthenticated) {
          state.error = action.payload || LOGIN_ERROR_MESSAGE;
        }
      });
  },
});

export const { logout, restoreSession, markInitialized } = authSlice.actions;
export default authSlice.reducer;
